import { ModalInput } from "components/atoms/input/modal/ModalInput";
import Input, { NumberInput } from "components/atoms/input/Input";
import { Text, ValidateWord } from "components/atoms/text/Text";
import { COLORS } from "styles/colors";
import {
  CheckInput,
  OptionTitleContainer,
  Row,
  RowModalInput,
} from "styles/ModalCommonStyled";
import styled from "styled-components";
import SelectComponent from "components/atoms/select/SelectComponent";
import { standardChannelOptionsByDuration } from "utils/options";
import { calcRem } from "styles/theme";
import { SubscriptionCommonWrapper } from "pages/subscription/SubscriptionStyled";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { LEVEL_TYPE } from "api/interfaces/commonInterface.interface";
import {
  KeyCost,
  KeyInfo,
  SUBSCRIPTION_TYPE,
} from "api/interfaces/subscription.interface";
import { formatUppercaseOnly, isNumber } from "utils/functions";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  changeServiceNameUnit,
  showCost,
  useKeyType,
} from "hook/subscription/useKeyType";
import { PRICE_REGEX } from "utils/regEx";

type Props = {
  keyInfo: KeyInfo;
  cost: KeyCost;
  onChangeKeyData: (name: string, value: any) => void;
  validateMsg: Record<string, string>;
};

export default function GenerateKeyInfo({
  keyInfo,
  cost,
  onChangeKeyData,
  validateMsg,
}: Props): JSX.Element {
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const { durationMonthOptions } = useKeyType();
  const [input, setInput] = useState<{
    [key: string]: number | string | boolean | undefined;
  }>({
    number: keyInfo.number,
    qty: keyInfo.qty,
    reference: keyInfo.reference,
    distributor: keyInfo.distributor,
    actualPrice: keyInfo.actualPrice,
    isFreeKey: keyInfo.isFreeKey,
  });

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let {
        target: { name, value, checked },
      } = e;
      if (name === "channels") {
        name = "number";
      }
      if (name === "keys") {
        name = "qty";
      }
      if (name === "actualPrice") {
        if (!PRICE_REGEX.test(value)) {
          return;
        }

        setInput((prev) => ({
          ...prev,
          actualPrice: value,
        }));
        return;
      }
      if (
        name !== "reference" &&
        name !== "distributor" &&
        name !== "isFreeKey" &&
        !isNumber(value) &&
        value
      ) {
        return;
      }
      if (name === "reference" || name === "distributor") {
        setInput((prev: any) => {
          return {
            ...prev,
            [name]: value,
          };
        });
        return;
      }
      if (name === "isFreeKey") {
        setInput((prev: any) => {
          return {
            ...prev,
            [name]: checked,
            actualPrice: checked ? "" : prev.actualPrice,
          };
        });
        return;
      }
      setInput((prev: any) => {
        return {
          ...prev,
          [name]: Number(value),
        };
      });
    },
    [onChangeKeyData, keyInfo.duration]
  );

  const isChannelOptional = useMemo(() => {
    return (
      keyInfo.keyType === SUBSCRIPTION_TYPE.STANDARD &&
      keyInfo.duration &&
      keyInfo.duration >= 12
    );
  }, [keyInfo.keyType, keyInfo.duration]);

  useEffect(() => {
    const price =
      input.actualPrice === "" || input.actualPrice === undefined
        ? undefined
        : Number(input.actualPrice);
    if (!isChannelOptional) {
      onChangeKeyData("number", input.number);
    }

    onChangeKeyData("qty", input.qty);
    onChangeKeyData("reference", input.reference);
    onChangeKeyData("distributor", input.distributor);
    onChangeKeyData("actualPrice", price);
    onChangeKeyData("isFreeKey", input.isFreeKey);
  }, [input, isChannelOptional, keyInfo.duration]);

  return (
    <Wrapper>
      <Text color={COLORS.BUTTONS1} className="info-text">
        Please select the Duration and select or fill out the Number of Channels
        and Quantity of the Subscription Keys to generate.
      </Text>

      <Row>
        <ModalInput label="Subscription Type" essential>
          <Input disabled value={formatUppercaseOnly(keyInfo.keyType)} />
        </ModalInput>
        <ModalInput
          label="Duration"
          essential
          isValidated={validateMsg.duration !== ""}
          validatedWord={validateMsg.duration}
        >
          <SelectComponent
            placeholder="Select duration"
            options={durationMonthOptions(
              keyInfo.keyType as SUBSCRIPTION_TYPE,
              selectedAccount.accountLevel === LEVEL_TYPE.DW
            )}
            value={durationMonthOptions(
              keyInfo.keyType as SUBSCRIPTION_TYPE,
              selectedAccount.accountLevel === LEVEL_TYPE.DW
            ).find((opt) => opt.value === String(keyInfo.duration))}
            onChange={(value: string) => {
              if (Number(value) < 12) {
                setInput((prev: any) => {
                  return {
                    ...prev,
                    isFreeKey: true,
                    actualPrice: "",
                  };
                });
              }
              onChangeKeyData("duration", Number(value));
            }}
            data-testid="subscription-modal-component-selects-number"
          />
        </ModalInput>
      </Row>
      <Row>
        <ModalInput
          label={changeServiceNameUnit(keyInfo.keyType)}
          essential
          isValidated={validateMsg.number !== ""}
          validatedWord={validateMsg.number}
        >
          {isChannelOptional ? (
            <SelectComponent
              placeholder="Select number of channels"
              options={
                keyInfo.duration
                  ? standardChannelOptionsByDuration(keyInfo.duration)
                  : []
              }
              value={
                keyInfo.duration
                  ? standardChannelOptionsByDuration(keyInfo.duration).find(
                      (opt) => opt.value === String(keyInfo.number)
                    )
                  : undefined
              }
              onChange={(value: string) =>
                onChangeKeyData("number", Number(value))
              }
              data-testid="subscription-modal-component-selects-change-key-data"
            />
          ) : (
            <NumberInput
              placeholder={
                keyInfo.keyType === SUBSCRIPTION_TYPE.STANDARD
                  ? "Enter number of channels"
                  : "Enter storage amount in TB"
              }
              name="channels"
              value={input.number as number}
              onChange={onChange}
              data-testid="subscription-modal-component-channels-change"
            />
          )}
        </ModalInput>
        <ModalInput
          label="Number of Keys"
          essential
          isValidated={validateMsg.qty !== ""}
          validatedWord={validateMsg.qty}
        >
          <NumberInput
            placeholder="Enter quantity of keys"
            name="keys"
            value={input.qty as number}
            onChange={onChange}
            data-testid="subscription-modal-component-keys-change"
          />
        </ModalInput>
      </Row>
      {selectedAccount.accountLevel === LEVEL_TYPE.DW && (
        <>
          <OptionTitleContainer>Optional</OptionTitleContainer>
          <Row>
            <ModalInput label="Reference Number">
              <Input
                name="reference"
                value={input.reference as string}
                placeholder="Enter reference Number"
                onChange={onChange}
                data-testid="subscription-modal-component-reference-change"
              />
            </ModalInput>
            <ModalInput label="Distributor">
              <Input
                name="distributor"
                value={input.distributor as string}
                placeholder="Enter distributor"
                onChange={onChange}
                data-testid="subscription-modal-component-distributor-change"
              />
            </ModalInput>
          </Row>
          <Row>
            <ModalInput
              label="Price"
              isValidated={validateMsg.actualPrice !== ""}
              validatedWord={validateMsg.actualPrice}
              half
            >
              <RowModalInput>
                <CheckInput>
                  <input
                    type="checkbox"
                    checked={input.isFreeKey as boolean}
                    name="isFreeKey"
                    onChange={onChange}
                    disabled={
                      keyInfo.duration ? keyInfo.duration < 12 : undefined
                    }
                    data-testid="subscription-modal-component-is-free-key-change"
                  />

                  <Text>Free Keys</Text>
                </CheckInput>
                <Input
                  name="actualPrice"
                  value={input.actualPrice as number}
                  placeholder="Enter price"
                  onChange={onChange}
                  type="text"
                  inputMode="decimal"
                  disabled={input.isFreeKey as boolean}
                  data-testid="subscription-modal-component-actual-price-change"
                />
              </RowModalInput>
            </ModalInput>
          </Row>
        </>
      )}

      {selectedAccount.accountLevel === LEVEL_TYPE.CP && (
        <Row className="info">
          <CostWrapper>
            <ModalInput label="Cost Per Month">
              <Text className="value">${showCost(cost.monthlyCost)}</Text>
            </ModalInput>
          </CostWrapper>
          <CostWrapper>
            <ModalInput label="Total Cost">
              <Text className="value">${showCost(cost.totalCost)}</Text>
            </ModalInput>
          </CostWrapper>
        </Row>
      )}

      {validateMsg.generateKeyInfo !== "" && (
        <ValidateWord data-testid="subscription-modal-generate-key-info-validate">
          {validateMsg.generateKeyInfo}
        </ValidateWord>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(SubscriptionCommonWrapper)`
  white-space: break-spaces;

  .info {
    flex-direction: row;
  }
  .info-text {
    white-space: break-spaces;
  }
`;

const CostWrapper = styled.div`
  background-color: ${({ theme }) => theme.COLORS.PRIMARY3};
  padding: 10px 20px;
  flex: 1;

  .common-modal-input {
    gap: 10px;
  }

  h2 {
    font-size: ${calcRem(32)};
    font-weight: 500;
  }
`;
